/**
 * Centralized Sport Configuration
 * Single source of truth for all sport-specific stat definitions, labels, and metadata.
 *
 * Sport *identity* and *common* metadata (canonical ids, aliases, display
 * labels, tie rules, scoring terminology) live in `@/config/sports/registry`.
 * This module consumes that registry so common points stay defined once.
 */

import {
  getScoringTerminology as getRegistryScoringTerminology,
  sportAllowsTies as registrySportAllowsTies,
  normalizeSport,
  type ScoringTerminology,
} from '@/config/sports/registry';

export type SportType = 'soccer' | 'basketball' | 'baseball' | 'softball' | 'flag_football';

export interface StatDefinition {
  key: string;
  label: string;
  isBasic?: boolean;
  isAdvanced?: boolean;
  isCalculated?: boolean; // Auto-calculated from other stats
  dependencies?: string[]; // Required stats for calculation
  format?: 'number' | 'percentage' | 'decimal' | 'time' | 'ratio' | 'innings';
  decimals?: number;
  positions?: string[]; // Which positions track this stat
  description?: string;
}

export interface SportConfig {
  name: string;
  allowsTies: boolean; // Whether this sport allows tie games
  scoringPeriods: string[];
  positions: string[];
  positionGroups: Record<string, string[]>; // e.g., { goalkeepers: ['GK'], outfield: ['DEF', 'MID', 'FWD'] }
  positionAliases: Record<string, string>; // e.g., { 'Goalkeeper': 'GK', 'Forward': 'FWD' }
  positionLabels: Record<string, string>; // e.g., { 'GK': 'Goalkeeper', 'FWD': 'Forward' } - reverse of aliases for display
  matchStats: {
    team: StatDefinition[];
    player: StatDefinition[];
  };
  seasonStats: {
    team: StatDefinition[];
    player: StatDefinition[];
  };
  defaultEnabledStats: {
    teamBasic: string[];
    teamAdvanced: string[];
    playerBasic: string[];
    playerAdvanced: string[];
    playerEssential?: string[]; // Subset of playerBasic for compact view
    teamEssential?: string[]; // Subset of teamBasic for compact view
  };
  statSynonyms: Record<string, string[]>; // Alternative names for same stat
  statDisplayOrder: {
    player: string[];      // Canonical order for player stats (ESPN/CBS standard)
    team: string[];        // Canonical order for team stats
    playerGK?: string[];   // Special ordering for soccer goalkeepers
  };
}

// Soccer Configuration
const soccerConfig: SportConfig = {
  name: 'Soccer',
  allowsTies: true, // Soccer commonly ends in ties
  scoringPeriods: ['1st Half', '2nd Half'],
  positions: ['GK', 'DEF', 'MID', 'FWD'],
  positionGroups: {
    goalkeepers: ['GK'],
    outfield: ['DEF', 'MID', 'FWD'],
  },
  positionAliases: {
    'Goalkeeper': 'GK',
    'goalkeeper': 'GK',
    'Defender': 'DEF',
    'defender': 'DEF',
    'Defense': 'DEF',
    'defense': 'DEF',
    'Midfielder': 'MID',
    'midfielder': 'MID',
    'Midfield': 'MID',
    'midfield': 'MID',
    'Forward': 'FWD',
    'forward': 'FWD',
    'Striker': 'FWD',
    'striker': 'FWD',
    'Attacker': 'FWD',
    'attacker': 'FWD',
  },
  positionLabels: {
    'GK': 'Goalkeeper',
    'DEF': 'Defender',
    'MID': 'Midfielder',
    'FWD': 'Forward',
  },
  matchStats: {
    team: [
      { key: 'goals', label: 'Goals', isBasic: true, format: 'number' },
      { key: 'goals_against', label: 'Goals Against', isBasic: true, format: 'number' },
      { key: 'assists', label: 'Assists', isBasic: true, format: 'number' },
      { key: 'shots', label: 'Shots', isBasic: true, format: 'number' },
      { key: 'shots_on_target', label: 'Shots on Target', isBasic: true, format: 'number' },
      { key: 'possession', label: 'Possession %', isBasic: true, format: 'percentage' },
      { key: 'passes', label: 'Passes', isBasic: true, format: 'number' },
      { key: 'passes_completed', label: 'Passes Completed', isBasic: true, format: 'number' },
      { key: 'corners', label: 'Corners', isBasic: true, format: 'number' },
      { key: 'fouls', label: 'Fouls', isBasic: true, format: 'number' },
      { key: 'yellow_cards', label: 'Yellow Cards', isBasic: true, format: 'number' },
      { key: 'red_cards', label: 'Red Cards', isBasic: true, format: 'number' },
      { key: 'shot_accuracy', label: 'Shot Accuracy', isAdvanced: true, isCalculated: true, dependencies: ['shots', 'shots_on_target'], format: 'percentage', decimals: 2 },
      { key: 'pass_accuracy', label: 'Pass Accuracy', isAdvanced: true, isCalculated: true, dependencies: ['passes', 'passes_completed'], format: 'percentage', decimals: 2 },
    ],
    player: [
      { key: 'goals', label: 'Goals', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'assists', label: 'Assists', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'shots', label: 'Shots', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'shots_on_target', label: 'Shots on Target', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'passes', label: 'Passes', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'passes_completed', label: 'Passes Completed', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'minutes', label: 'Minutes', isBasic: true, format: 'number' },
      { key: 'saves', label: 'Saves', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'goals_conceded', label: 'Goals Conceded', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'shots_on_target_faced', label: 'SOT Faced', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'yellow_cards', label: 'Yellow Cards', isBasic: true, format: 'number' },
      { key: 'red_cards', label: 'Red Cards', isBasic: true, format: 'number' },
      { key: 'fouls', label: 'Fouls', isBasic: true, format: 'number' },
      { key: 'offsides', label: 'Offsides', isBasic: true, format: 'number', positions: ['FWD', 'MID'] },
      { key: 'clean_sheet', label: 'Clean Sheet', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'save_percentage', label: 'Save %', isAdvanced: true, isCalculated: true, dependencies: ['saves', 'shots_on_target_faced'], format: 'percentage', decimals: 2, positions: ['GK'] },
      { key: 'shot_accuracy', label: 'Shot Accuracy', isAdvanced: true, isCalculated: true, dependencies: ['shots', 'shots_on_target'], format: 'percentage', decimals: 2, positions: ['DEF', 'MID', 'FWD'] },
      { key: 'goal_conversion', label: 'Goal Conversion', isAdvanced: true, isCalculated: true, dependencies: ['goals', 'shots'], format: 'percentage', decimals: 2, positions: ['DEF', 'MID', 'FWD'] },
      { key: 'pass_accuracy', label: 'Pass Accuracy', isAdvanced: true, isCalculated: true, dependencies: ['passes', 'passes_completed'], format: 'percentage', decimals: 2, positions: ['DEF', 'MID', 'FWD'] },
    ],
  },
  seasonStats: {
    team: [
      { key: 'wins', label: 'Wins', isBasic: true, format: 'number' },
      { key: 'losses', label: 'Losses', isBasic: true, format: 'number' },
      { key: 'ties', label: 'Ties', isBasic: true, format: 'number' },
      { key: 'goals_for', label: 'Goals For', isBasic: true, format: 'number' },
      { key: 'goals_against', label: 'Goals Against', isBasic: true, format: 'number' },
      { key: 'goal_difference', label: 'Goal Difference', isAdvanced: true, isCalculated: true, dependencies: ['goals_for', 'goals_against'], format: 'number' },
      { key: 'clean_sheets', label: 'Clean Sheets', isAdvanced: true, dependencies: ['goals_against'], format: 'number' },
    ],
    player: [
      { key: 'games_played', label: 'Games', isBasic: true, format: 'number' },
      { key: 'goals', label: 'Goals', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'assists', label: 'Assists', isBasic: true, format: 'number', positions: ['DEF', 'MID', 'FWD'] },
      { key: 'minutes', label: 'Minutes', isBasic: true, format: 'number' },
      { key: 'saves', label: 'Saves', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'goals_conceded', label: 'Goals Conceded', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'clean_sheets', label: 'Clean Sheets', isBasic: true, format: 'number', positions: ['GK'] },
      { key: 'goals_per_90', label: 'Goals/90', isAdvanced: true, isCalculated: true, dependencies: ['goals', 'minutes'], format: 'decimal', decimals: 2, positions: ['DEF', 'MID', 'FWD'] },
      { key: 'assists_per_90', label: 'Assists/90', isAdvanced: true, isCalculated: true, dependencies: ['assists', 'minutes'], format: 'decimal', decimals: 2, positions: ['DEF', 'MID', 'FWD'] },
      { key: 'gaa_per_90', label: 'GAA/90', isAdvanced: true, isCalculated: true, dependencies: ['goals_conceded', 'minutes'], format: 'decimal', decimals: 2, positions: ['GK'] },
      { key: 'save_percentage', label: 'Save %', isAdvanced: true, isCalculated: true, dependencies: ['saves', 'goals_conceded'], format: 'percentage', decimals: 2, positions: ['GK'] },
    ],
  },
  defaultEnabledStats: {
    teamBasic: ['goals', 'shots', 'shots_on_target', 'possession', 'passes', 'passes_completed', 'corners', 'fouls'],
    teamAdvanced: ['shot_accuracy'],
    playerBasic: ['goals', 'assists', 'shots', 'shots_on_target', 'minutes', 'saves', 'goals_conceded', 'yellow_cards', 'red_cards'],
    playerAdvanced: ['shot_accuracy', 'goal_conversion', 'save_percentage', 'goals_per_90', 'assists_per_90', 'gaa_per_90'],
    playerEssential: ['games_played', 'minutes', 'goals', 'assists', 'shots', 'shots_on_target'],
    teamEssential: ['games_played', 'goals', 'goals_against', 'shots', 'shots_on_target', 'possession'],
  },
  statSynonyms: {
    goals: ['goals_for', 'goals_scored', 'gf'],
    goals_against: ['goals_conceded', 'ga'],
    assists: ['ast'],
    shots_on_target: ['sot', 'shots_on_goal'],
    pass_accuracy: ['passing_accuracy', 'pass_completion'],
    minutes: ['minutes_played', 'min'],
    saves: ['saves_made', 's'],
    clean_sheet: ['clean_sheets'],
    shots_on_target_faced: ['sot_faced'],
    yellow_cards: ['yellow', 'yc'],
    red_cards: ['red', 'rc'],
    possession: ['ball_possession', 'poss'],
    passes: ['pass'],
    passes_completed: ['completed_passes'],
    // Advanced stat synonyms
    save_percentage: ['save_percent', 'save_pct', 'save%'],
    gaa_per_90: ['gaa90', 'gaa_per_90min', 'goals_against_average_per_90'],
    shot_accuracy: ['shooting_accuracy', 'shot_pct'],
    goal_conversion: ['goal_conversion_rate', 'conversion_rate'],
  },
  statDisplayOrder: {
    player: ['games_played', 'games_started', 'minutes', 'goals', 'goals_per_90', 'assists', 'assists_per_90', 'shots', 'shots_on_target', 'shot_accuracy', 'goal_conversion', 'passes', 'passes_completed', 'pass_accuracy', 'tackles', 'interceptions', 'fouls', 'yellow_cards', 'red_cards'],
    playerGK: ['games_played', 'games_started', 'minutes', 'goals_conceded', 'gaa_per_90', 'saves', 'save_percentage', 'clean_sheets'],
    team: ['games_played', 'wins', 'draws', 'losses', 'goals', 'goals_against', 'goal_difference', 'shots', 'shots_on_target', 'shot_accuracy', 'possession', 'passes', 'passes_completed', 'pass_accuracy', 'corners', 'fouls', 'yellow_cards', 'red_cards'],
  }
};

// Basketball Configuration
const basketballConfig: SportConfig = {
  name: 'Basketball',
  allowsTies: false, // Basketball uses overtime
  scoringPeriods: ['Q1', 'Q2', 'Q3', 'Q4'],
  positions: ['PG', 'SG', 'SF', 'PF', 'C'],
  positionGroups: {
    guards: ['PG', 'SG'],
    forwards: ['SF', 'PF'],
    centers: ['C'],
  },
  positionAliases: {
    'Point Guard': 'PG',
    'point guard': 'PG',
    'Shooting Guard': 'SG',
    'shooting guard': 'SG',
    'Small Forward': 'SF',
    'small forward': 'SF',
    'Power Forward': 'PF',
    'power forward': 'PF',
    'Center': 'C',
    'center': 'C',
    'Guard': 'PG',
    'guard': 'PG',
    'Forward': 'SF',
    'forward': 'SF',
  },
  positionLabels: {
    'PG': 'Point Guard',
    'SG': 'Shooting Guard',
    'SF': 'Small Forward',
    'PF': 'Power Forward',
    'C': 'Center',
  },
  matchStats: {
    team: [
      { key: 'points', label: 'Points', isBasic: true, format: 'number' },
      { key: 'fgm', label: 'FG Made', isBasic: true, format: 'number' },
      { key: 'fga', label: 'FG Attempted', isBasic: true, format: 'number' },
      { key: 'tpm', label: '3PT Made', isBasic: true, format: 'number' },
      { key: 'tpa', label: '3PT Attempted', isBasic: true, format: 'number' },
      { key: 'ftm', label: 'FT Made', isBasic: true, format: 'number' },
      { key: 'fta', label: 'FT Attempted', isBasic: true, format: 'number' },
      { key: 'oreb', label: 'OffReb', isBasic: true, format: 'number' },
      { key: 'dreb', label: 'DefReb', isBasic: true, format: 'number' },
      { key: 'treb', label: 'Total Rebounds', isBasic: true, format: 'number' },
      { key: 'assists', label: 'Assists', isBasic: true, format: 'number' },
      { key: 'steals', label: 'Steals', isBasic: true, format: 'number' },
      { key: 'blocks', label: 'Blocks', isBasic: true, format: 'number' },
      { key: 'turnovers', label: 'Turnovers', isBasic: true, format: 'number' },
      { key: 'personal_fouls', label: 'Personal Fouls', isBasic: true, format: 'number' },
      { key: 'fg_percentage', label: 'FG%', isBasic: true, isCalculated: true, dependencies: ['fgm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'three_pt_percentage', label: '3PT%', isBasic: true, isCalculated: true, dependencies: ['tpm', 'tpa'], format: 'percentage', decimals: 1 },
      { key: 'ft_percentage', label: 'FT%', isBasic: true, isCalculated: true, dependencies: ['ftm', 'fta'], format: 'percentage', decimals: 1 },
      { key: 'efg_percentage', label: 'eFG%', isAdvanced: true, isCalculated: true, dependencies: ['fgm', 'tpm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'ts_percentage', label: 'TS%', isAdvanced: true, isCalculated: true, dependencies: ['points', 'fga', 'fta'], format: 'percentage', decimals: 1 },
      { key: 'team_assist_pct', label: 'Team Assist %', isAdvanced: true, isCalculated: true, dependencies: ['assists', 'fgm'], format: 'percentage', decimals: 1 },
      { key: 'oreb_pct', label: 'OREB %', isAdvanced: true, isCalculated: true, dependencies: ['oreb', 'opponent_dreb'], format: 'percentage', decimals: 1 },
      { key: 'rebounding_margin', label: 'Rebounding Margin', isAdvanced: true, isCalculated: true, dependencies: ['treb', 'opponent_treb'], format: 'number' },
    ],
    player: [
      { key: 'points', label: 'PTS', isBasic: true, format: 'number' },
      { key: 'fgm', label: 'FGM', isBasic: true, format: 'number' },
      { key: 'fga', label: 'FGA', isBasic: true, format: 'number' },
      { key: 'fg_percentage', label: 'FG%', isBasic: true, isCalculated: true, dependencies: ['fgm', 'fga'], format: 'percentage', decimals: 2 },
      { key: 'tpm', label: '3PM', isBasic: true, format: 'number' },
      { key: 'tpa', label: '3PA', isBasic: true, format: 'number' },
      { key: 'three_pt_percentage', label: '3PT%', isBasic: true, isCalculated: true, dependencies: ['tpm', 'tpa'], format: 'percentage', decimals: 2 },
      { key: 'ftm', label: 'FTM', isBasic: true, format: 'number' },
      { key: 'fta', label: 'FTA', isBasic: true, format: 'number' },
      { key: 'ft_percentage', label: 'FT%', isBasic: true, isCalculated: true, dependencies: ['ftm', 'fta'], format: 'percentage', decimals: 2 },
      { key: 'treb', label: 'REB', isBasic: true, format: 'number' },
      { key: 'oreb', label: 'OREB', isBasic: true, format: 'number' },
      { key: 'dreb', label: 'DREB', isBasic: true, format: 'number' },
      { key: 'assists', label: 'AST', isBasic: true, format: 'number' },
      { key: 'steals', label: 'STL', isBasic: true, format: 'number' },
      { key: 'blocks', label: 'BLK', isBasic: true, format: 'number' },
      { key: 'turnovers', label: 'TO', isBasic: true, format: 'number' },
      { key: 'personal_fouls', label: 'PF', isBasic: true, format: 'number' },
      { key: 'minutes', label: 'MIN', isBasic: true, format: 'number' },
      { key: 'efg_percentage', label: 'eFG%', isAdvanced: true, isCalculated: true, dependencies: ['fgm', 'tpm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'ts_percentage', label: 'TS%', isAdvanced: true, isCalculated: true, dependencies: ['points', 'fga', 'fta'], format: 'percentage', decimals: 1 },
      { key: 'ast_to_ratio', label: 'AST/TO', isAdvanced: true, isCalculated: true, dependencies: ['assists', 'turnovers'], format: 'decimal', decimals: 2 },
    ],
  },
  seasonStats: {
    team: [
      { key: 'wins', label: 'Wins', isBasic: true, format: 'number' },
      { key: 'losses', label: 'Losses', isBasic: true, format: 'number' },
      { key: 'points_for', label: 'Points For', isBasic: true, format: 'number' },
      { key: 'points_against', label: 'Points Against', isBasic: true, format: 'number' },
      { key: 'fg_percentage', label: 'FG%', isBasic: true, isCalculated: true, dependencies: ['fgm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'three_pt_percentage', label: '3PT%', isBasic: true, isCalculated: true, dependencies: ['tpm', 'tpa'], format: 'percentage', decimals: 1 },
      { key: 'ppg', label: 'PPG', isAdvanced: true, isCalculated: true, dependencies: ['points_for', 'games_played'], format: 'decimal', decimals: 2 },
    ],
    player: [
      { key: 'games_played', label: 'Games', isBasic: true, format: 'number' },
      { key: 'points', label: 'Points', isBasic: true, format: 'number' },
      { key: 'rebounds', label: 'Rebounds', isBasic: true, format: 'number' },
      { key: 'assists', label: 'Assists', isBasic: true, format: 'number' },
      { key: 'steals', label: 'Steals', isBasic: true, format: 'number' },
      { key: 'blocks', label: 'Blocks', isBasic: true, format: 'number' },
      { key: 'turnovers', label: 'Turnovers', isBasic: true, format: 'number' },
      { key: 'minutes', label: 'Minutes', isBasic: true, format: 'number' },
      { key: 'fg_percentage', label: 'FG%', isBasic: true, isCalculated: true, dependencies: ['fgm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'three_pt_percentage', label: '3PT%', isBasic: true, isCalculated: true, dependencies: ['tpm', 'tpa'], format: 'percentage', decimals: 1 },
      { key: 'ft_percentage', label: 'FT%', isBasic: true, isCalculated: true, dependencies: ['ftm', 'fta'], format: 'percentage', decimals: 1 },
      { key: 'ppg', label: 'PPG', isAdvanced: true, isCalculated: true, dependencies: ['points', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'rpg', label: 'RPG', isAdvanced: true, isCalculated: true, dependencies: ['rebounds', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'apg', label: 'APG', isAdvanced: true, isCalculated: true, dependencies: ['assists', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'spg', label: 'SPG', isAdvanced: true, isCalculated: true, dependencies: ['steals', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'bpg', label: 'BPG', isAdvanced: true, isCalculated: true, dependencies: ['blocks', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'tpg', label: 'TPG', isAdvanced: true, isCalculated: true, dependencies: ['turnovers', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'mpg', label: 'MPG', isAdvanced: true, isCalculated: true, dependencies: ['minutes', 'games_played'], format: 'decimal', decimals: 1 },
      { key: 'efg_percentage', label: 'eFG%', isAdvanced: true, isCalculated: true, dependencies: ['fgm', 'tpm', 'fga'], format: 'percentage', decimals: 1 },
      { key: 'ts_percentage', label: 'TS%', isAdvanced: true, isCalculated: true, dependencies: ['points', 'fga', 'fta'], format: 'percentage', decimals: 1 },
      { key: 'ast_to_ratio', label: 'AST/TO', isAdvanced: true, isCalculated: true, dependencies: ['assists', 'turnovers'], format: 'decimal', decimals: 2 },
      { key: 'efficiency', label: 'Efficiency', isAdvanced: true, isCalculated: true, dependencies: ['points', 'rebounds', 'assists', 'steals', 'blocks', 'fgm', 'fga', 'turnovers', 'games_played'], format: 'decimal', decimals: 1 },
    ],
  },
  defaultEnabledStats: {
    teamBasic: ['treb', 'assists', 'steals', 'blocks', 'turnovers', 'fg_percentage'],
    teamAdvanced: ['efg_percentage', 'ts_percentage', 'team_assist_pct', 'oreb_pct', 'rebounding_margin'],
    playerBasic: ['points', 'fgm', 'fga', 'fg_percentage', 'tpm', 'tpa', 'three_pt_percentage', 'ftm', 'fta', 'ft_percentage', 'treb', 'oreb', 'dreb', 'assists', 'steals', 'blocks', 'turnovers', 'minutes'],
    playerEssential: ['games_played', 'minutes', 'points', 'fg_percentage', 'three_pt_percentage', 'ft_percentage', 'treb', 'assists'],
    playerAdvanced: ['efg_percentage', 'ts_percentage', 'ast_to_ratio', 'ppg', 'rpg', 'apg', 'spg', 'bpg', 'tpg', 'mpg', 'efficiency'],
    teamEssential: ['games_played', 'points', 'fg_percentage', 'three_pt_percentage', 'treb', 'assists'],
  },
  statSynonyms: {
    // Primary stats with ALL common abbreviations
    points: ['pts', 'pt', 'p'],
    assists: ['ast', 'a', 'assist'],
    steals: ['stl', 's', 'steal'],
    blocks: ['blk', 'b', 'block'],
    turnovers: ['to', 'tov', 't', 'turnover'],
    minutes: ['min', 'm', 'mins'],
    personal_fouls: ['pf', 'fouls', 'f', 'personalfouls'],
    fgm: ['field_goals_made', 'fg_made', 'fieldgoalsmade'],
    fga: ['field_goals_attempted', 'fg_attempted', 'fieldgoalsattempted'],
    tpm: ['three_pointers_made', 'tp_made', '3pm', '3p_made', 'threes_made', 'threesmade'],
    tpa: ['three_pointers_attempted', 'tp_attempted', '3pa', '3p_attempted', 'threes_attempted', 'threesattempted'],
    ftm: ['free_throws_made', 'ft_made', 'freethrowsmade'],
    fta: ['free_throws_attempted', 'ft_attempted', 'freethrowsattempted'],
    oreb: ['offensive_rebounds', 'off_reb', 'offensiverebounds'],
    dreb: ['defensive_rebounds', 'def_reb', 'defensiverebounds'],
    treb: ['total_rebounds', 'reb', 'rebounds', 'totalrebounds'],
    // Advanced stat synonyms to bridge backoffice and display keys
    fg_percentage: ['fg_pct', 'fg%', 'field_goal_percentage', 'fgpct', 'fg_percent'],
    three_pt_percentage: ['tp_pct', '3p_pct', '3pt_pct', '3p%', 'three_point_percentage', 'threeptpct', 'three_pt_pct', 'tp%', 'tp_percent'],
    ft_percentage: ['ft_pct', 'ft%', 'free_throw_percentage', 'ftpct', 'ft_percent'],
    efg_percentage: ['efg_pct', 'efg%', 'effective_field_goal_percentage', 'efgpct'],
    ts_percentage: ['ts_pct', 'ts%', 'true_shooting_percentage', 'tspct'],
    ast_to_ratio: ['ast_to', 'ast/to', 'assist_turnover_ratio', 'asttoratio'],
    efficiency: ['eff', 'per', 'player_efficiency_rating', 'eff_rating'],
    team_assist_pct: ['team_ast_percent', 'ast_pct', 'assist_percentage', 'team_ast_pct'],
    oreb_pct: ['orb_pct', 'oreb_percent', 'orb%', 'offensive_rebound_percentage'],
    rebounding_margin: ['reb_margin', 'reb_diff', 'rebound_differential'],
  },
  statDisplayOrder: {
    player: ['games_played', 'games_started', 'minutes', 'mpg', 'points', 'ppg', 'fgm', 'fga', 'fg_percentage', 'efg_percentage', 'tpm', 'tpa', 'three_pt_percentage', 'ftm', 'fta', 'ft_percentage', 'ts_percentage', 'treb', 'rpg', 'oreb', 'dreb', 'assists', 'apg', 'ast_to_ratio', 'steals', 'spg', 'blocks', 'bpg', 'turnovers', 'tpg', 'personal_fouls', 'efficiency'],
    team: ['games_played', 'wins', 'losses', 'points', 'ppg', 'fgm', 'fga', 'fg_percentage', 'efg_percentage', 'tpm', 'tpa', 'three_pt_percentage', 'ftm', 'fta', 'ft_percentage', 'ts_percentage', 'treb', 'oreb', 'dreb', 'assists', 'team_assist_pct', 'oreb_pct', 'rebounding_margin', 'steals', 'blocks', 'turnovers', 'personal_fouls'],
  }
};

// Baseball Configuration
const baseballConfig: SportConfig = {
  name: 'Baseball',
  allowsTies: false, // Baseball uses extra innings
  scoringPeriods: ['1', '2', '3', '4', '5', '6', '7', '8', '9'],
  positions: ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'],
  positionGroups: {
    pitchers: ['P'],
    batters: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'],
  },
  positionAliases: {
    'Pitcher': 'P',
    'pitcher': 'P',
    'PITCHER': 'P',
    'Catcher': 'C',
    'catcher': 'C',
    'CATCHER': 'C',
    'catch': 'C',
    'First Base': '1B',
    'first base': '1B',
    'first baseman': '1B',
    'firstbase': '1B',
    'Second Base': '2B',
    'second base': '2B',
    'second baseman': '2B',
    'secondbase': '2B',
    'Third Base': '3B',
    'third base': '3B',
    'third baseman': '3B',
    'thirdbase': '3B',
    'Shortstop': 'SS',
    'shortstop': 'SS',
    'short stop': 'SS',
    'Left Field': 'LF',
    'left field': 'LF',
    'left fielder': 'LF',
    'leftfield': 'LF',
    'Center Field': 'CF',
    'center field': 'CF',
    'center fielder': 'CF',
    'centerfield': 'CF',
    'Right Field': 'RF',
    'right field': 'RF',
    'right fielder': 'RF',
    'rightfield': 'RF',
    'Designated Hitter': 'DH',
    'designated hitter': 'DH',
    'DH': 'DH',
    'Outfield': 'LF',
    'outfield': 'LF',
    'OF': 'LF',
    'Infield': '2B',
    'infield': '2B',
    'IF': '2B',
  },
  positionLabels: {
    'P': 'Pitcher',
    'C': 'Catcher',
    '1B': 'First Base',
    '2B': 'Second Base',
    '3B': 'Third Base',
    'SS': 'Shortstop',
    'LF': 'Left Field',
    'CF': 'Center Field',
    'RF': 'Right Field',
    'DH': 'Designated Hitter',
  },
  matchStats: {
    team: [
      { key: 'runs', label: 'Runs', isBasic: true, format: 'number' },
      { key: 'hits', label: 'Hits', isBasic: true, format: 'number' },
      { key: 'errors', label: 'Errors', isBasic: true, format: 'number' },
      { key: 'home_runs', label: 'Home Runs', isBasic: true, format: 'number' },
      { key: 'doubles', label: 'Doubles', isBasic: true, format: 'number' },
      { key: 'triples', label: 'Triples', isBasic: true, format: 'number' },
      { key: 'walks', label: 'Walks', isBasic: true, format: 'number' },
      { key: 'strikeouts', label: 'Strikeouts', isBasic: true, format: 'number' },
    ],
    player: [
      // Batting stats
      { key: 'at_bats', label: 'At Bats', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'hits', label: 'Hits', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'runs', label: 'Runs', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'rbis', label: 'RBIs', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'doubles', label: 'Doubles', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'triples', label: 'Triples', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'home_runs', label: 'Home Runs', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'walks', label: 'Walks', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'strikeouts', label: 'Strikeouts', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'stolen_bases', label: 'Stolen Bases', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'caught_stealing', label: 'Caught Stealing', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'hit_by_pitch', label: 'Hit By Pitch', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'sacrifice_flies', label: 'Sac Flies', isBasic: true, format: 'number', positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'batting_average', label: 'AVG', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'at_bats'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'on_base_percentage', label: 'OBP', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'walks', 'hit_by_pitch', 'at_bats', 'sacrifice_flies'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'slugging_percentage', label: 'SLG', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'doubles', 'triples', 'home_runs', 'at_bats'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      // Pitching stats - descriptive labels for scoring interface clarity
      { key: 'wins', label: 'Wins', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'losses', label: 'Losses', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'saves', label: 'Saves', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'holds', label: 'Holds', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'innings_pitched', label: 'Innings Pitched', isBasic: true, format: 'innings', positions: ['P'] },
      { key: 'earned_runs', label: 'Earned Runs', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'strikeouts_pitched', label: 'Strikeouts (P)', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'walks_allowed', label: 'Walks Allowed', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'hits_allowed', label: 'Hits Allowed', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'home_runs_allowed', label: 'HR Allowed', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'complete_games', label: 'Complete Games', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'shutouts', label: 'Shutouts', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'era', label: 'ERA', isAdvanced: true, isCalculated: true, dependencies: ['earned_runs', 'innings_pitched'], format: 'decimal', decimals: 2, positions: ['P'] },
      { key: 'whip', label: 'WHIP', isAdvanced: true, isCalculated: true, dependencies: ['walks_allowed', 'hits_allowed', 'innings_pitched'], format: 'decimal', decimals: 2, positions: ['P'] },
    ],
  },
  seasonStats: {
    team: [
      { key: 'wins', label: 'Wins', isBasic: true, format: 'number' },
      { key: 'losses', label: 'Losses', isBasic: true, format: 'number' },
      { key: 'runs_for', label: 'Runs For', isBasic: true, format: 'number' },
      { key: 'runs_against', label: 'Runs Against', isBasic: true, format: 'number' },
    ],
    player: [
      { key: 'games_played', label: 'Games', isBasic: true, format: 'number' },
      { key: 'at_bats', label: 'At Bats', isBasic: true, format: 'number' },
      { key: 'hits', label: 'Hits', isBasic: true, format: 'number' },
      { key: 'runs', label: 'Runs', isBasic: true, format: 'number' },
      { key: 'rbis', label: 'RBIs', isBasic: true, format: 'number' },
      { key: 'home_runs', label: 'HR', isBasic: true, format: 'number' },
      { key: 'walks', label: 'Walks', isBasic: true, format: 'number' },
      { key: 'strikeouts', label: 'Strikeouts', isBasic: true, format: 'number' },
      { key: 'stolen_bases', label: 'SB', isBasic: true, format: 'number' },
      { key: 'wins', label: 'W', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'losses', label: 'L', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'saves', label: 'SV', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'innings_pitched', label: 'IP', isBasic: true, format: 'innings', positions: ['P'] },
      { key: 'earned_runs', label: 'ER', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'strikeouts_pitched', label: 'K', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'walks_allowed', label: 'BB', isBasic: true, format: 'number', positions: ['P'] },
      { key: 'batting_average', label: 'AVG', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'at_bats'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'on_base_percentage', label: 'OBP', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'walks', 'hit_by_pitch', 'at_bats', 'sacrifice_flies'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'slugging_percentage', label: 'SLG', isAdvanced: true, isCalculated: true, dependencies: ['hits', 'doubles', 'triples', 'home_runs', 'at_bats'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'ops', label: 'OPS', isAdvanced: true, isCalculated: true, dependencies: ['on_base_percentage', 'slugging_percentage'], format: 'decimal', decimals: 3, positions: ['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH'] },
      { key: 'era', label: 'ERA', isAdvanced: true, isCalculated: true, dependencies: ['earned_runs', 'innings_pitched'], format: 'decimal', decimals: 2, positions: ['P'] },
      { key: 'whip', label: 'WHIP', isAdvanced: true, isCalculated: true, dependencies: ['walks_allowed', 'hits_allowed', 'innings_pitched'], format: 'decimal', decimals: 2, positions: ['P'] },
    ],
  },
  defaultEnabledStats: {
    teamBasic: ['runs', 'hits', 'errors'],
    teamAdvanced: [],
    playerBasic: ['at_bats', 'hits', 'runs', 'rbis', 'doubles', 'triples', 'home_runs', 'walks', 'strikeouts', 'stolen_bases', 'wins', 'losses', 'saves', 'innings_pitched', 'earned_runs', 'strikeouts_pitched', 'walks_allowed', 'hits_allowed'],
    playerAdvanced: ['batting_average', 'on_base_percentage', 'slugging_percentage', 'era', 'whip'],
    playerEssential: ['games_played', 'at_bats', 'hits', 'runs', 'rbis', 'home_runs', 'batting_average'],
    teamEssential: ['games_played', 'runs', 'hits', 'home_runs', 'batting_average'],
  },
  statSynonyms: {
    // Batting stats - comprehensive bidirectional mapping
    at_bats: ['ab'],
    hits: ['h'],
    singles: ['1b'],
    doubles: ['2b'],
    triples: ['3b'],
    home_runs: ['hr', 'homers'],
    runs: ['r', 'runs_scored'],
    rbis: ['rbi', 'runs_batted_in'],
    walks: ['bb', 'bases_on_balls'],
    strikeouts: ['k', 'so', 'k_batter'],
    hit_by_pitch: ['hbp'],
    stolen_bases: ['sb'],
    caught_stealing: ['cs'],
    sacrifice_flies: ['sf'],
    errors: ['e'],
    
    // Reverse mappings - sportStats.ts keys to canonical
    ab: ['at_bats'],
    h: ['hits'],
    '1b': ['singles'],
    '2b': ['doubles'],
    '3b': ['triples'],
    hr: ['home_runs'],
    r: ['runs'],
    rbi: ['rbis'],
    bb: ['walks'],
    k: ['strikeouts'],
    so: ['strikeouts'],
    k_batter: ['strikeouts'],
    hbp: ['hit_by_pitch'],
    sb: ['stolen_bases'],
    cs: ['caught_stealing'],
    sf: ['sacrifice_flies'],
    e: ['errors'],
    
    // Pitching stats
    wins: ['w'],
    losses: ['l'],
    saves: ['sv'],
    holds: ['hld'],
    innings_pitched: ['ip'],
    earned_runs: ['er'],
    strikeouts_pitched: ['k_pitcher'],
    walks_allowed: ['bb_allowed'],
    hits_allowed: ['h_allowed'],
    home_runs_allowed: ['hr_allowed'],
    
    // Reverse pitching mappings
    w: ['wins'],
    l: ['losses'],
    sv: ['saves'],
    hld: ['holds'],
    ip: ['innings_pitched'],
    er: ['earned_runs'],
    k_pitcher: ['strikeouts_pitched'],
    bb_allowed: ['walks_allowed'],
    h_allowed: ['hits_allowed'],
    hr_allowed: ['home_runs_allowed'],
    
    // Team stats
    runs_scored: ['runs', 'r'],
    runs_allowed: ['runs_against'],
    
    // Advanced stat synonyms
    batting_average: ['avg', 'ba', 'team_avg', 'team_batting_average'],
    on_base_percentage: ['obp', 'on_base_pct'],
    slugging_percentage: ['slg', 'slugging_pct'],
    ops: ['on_base_plus_slugging'],
    era: ['earned_run_average', 'team_era', 'team_earned_run_average'],
    whip: ['walks_hits_per_inning', 'team_whip'],
    k_per9: ['strikeouts_per_9', 'k_per_9'],
    
    // Team-level synonyms (used in settings UI) - bidirectional
    team_avg: ['batting_average', 'team_batting_average', 'avg', 'ba'],
    team_era: ['era', 'team_earned_run_average', 'earned_run_average'],
    team_whip: ['whip', 'walks_hits_per_inning'],
  },
  statDisplayOrder: {
    player: ['games_played', 'at_bats', 'runs', 'hits', 'doubles', 'triples', 'home_runs', 'rbis', 'walks', 'strikeouts', 'stolen_bases', 'caught_stealing', 'batting_average', 'on_base_percentage', 'slugging_percentage', 'ops', 'wins', 'losses', 'saves', 'holds', 'games_started', 'innings_pitched', 'hits_allowed', 'runs_allowed', 'earned_runs', 'home_runs_allowed', 'walks_allowed', 'strikeouts_pitched', 'complete_games', 'shutouts', 'era', 'whip', 'quality_starts'],
    team: ['games_played', 'runs', 'hits', 'doubles', 'triples', 'home_runs', 'rbis', 'walks', 'strikeouts', 'stolen_bases', 'batting_average', 'on_base_percentage', 'slugging_percentage', 'wins', 'losses', 'saves', 'innings_pitched', 'earned_runs', 'era', 'whip'],
  }
};

// Softball Configuration (extends baseball with slight modifications)
const softballConfig: SportConfig = {
  ...baseballConfig,
  name: 'Softball',
  scoringPeriods: ['1', '2', '3', '4', '5', '6', '7'], // 7 innings instead of 9
};

// Flag Football Configuration
const flagFootballConfig: SportConfig = {
  name: 'Flag Football',
  allowsTies: true, // Flag football can end in ties
  scoringPeriods: ['1st Half', '2nd Half'],
  positions: ['QB', 'C', 'WR', 'RB', 'SLOT', 'RUSH', 'DB', 'S', 'CB', 'LB', 'UTIL', 'ATH'],
  positionGroups: {
    offense: ['QB', 'C', 'WR', 'RB', 'SLOT'],
    defense: ['RUSH', 'DB', 'S', 'CB', 'LB'],
    utility: ['UTIL', 'ATH'],
    quarterbacks: ['QB'],
  },
  positionAliases: {
    'Quarterback': 'QB',
    'quarterback': 'QB',
    'Center': 'C',
    'center': 'C',
    'Wide Receiver': 'WR',
    'wide receiver': 'WR',
    'receiver': 'WR',
    'Running Back': 'RB',
    'running back': 'RB',
    'Slot Receiver': 'SLOT',
    'slot receiver': 'SLOT',
    'slot': 'SLOT',
    'Rusher': 'RUSH',
    'rusher': 'RUSH',
    'rush': 'RUSH',
    'Defensive Back': 'DB',
    'defensive back': 'DB',
    'Safety': 'S',
    'safety': 'S',
    'Cornerback': 'CB',
    'cornerback': 'CB',
    'corner': 'CB',
    'Linebacker': 'LB',
    'linebacker': 'LB',
    'Utility': 'UTIL',
    'utility': 'UTIL',
    'Athlete': 'ATH',
    'athlete': 'ATH',
  },
  positionLabels: {
    'QB': 'Quarterback',
    'C': 'Center',
    'WR': 'Wide Receiver',
    'RB': 'Running Back',
    'SLOT': 'Slot Receiver',
    'RUSH': 'Rusher',
    'DB': 'Defensive Back',
    'S': 'Safety',
    'CB': 'Cornerback',
    'LB': 'Linebacker',
    'UTIL': 'Utility',
    'ATH': 'Athlete',
  },
  matchStats: {
    team: [
      { key: 'points', label: 'Points', isBasic: true, format: 'number' },
      { key: 'touchdowns', label: 'Touchdowns', isBasic: true, format: 'number' },
      { key: 'extra_points_1pt', label: '1PT Conv', isBasic: true, format: 'number' },
      { key: 'extra_points_2pt', label: '2PT Conv', isBasic: true, format: 'number' },
      { key: 'safeties', label: 'Safeties', isBasic: true, format: 'number' },
      { key: 'total_yards', label: 'Total Yards', isBasic: true, format: 'number' },
      { key: 'pass_yards', label: 'Pass Yards', isBasic: true, format: 'number' },
      { key: 'rush_yards', label: 'Rush Yards', isBasic: true, format: 'number' },
      { key: 'first_downs', label: 'First Downs', isBasic: true, format: 'number' },
      { key: 'turnovers', label: 'Turnovers', isBasic: true, format: 'number' },
      { key: 'takeaways', label: 'Takeaways', isBasic: true, format: 'number' },
      { key: 'sacks', label: 'Sacks', isBasic: true, format: 'number' },
      { key: 'interceptions', label: 'Interceptions', isBasic: true, format: 'number' },
      { key: 'third_down_attempts', label: '3rd Down Att', isBasic: true, format: 'number' },
      { key: 'third_down_conversions', label: '3rd Down Conv', isBasic: true, format: 'number' },
      { key: 'fourth_down_attempts', label: '4th Down Att', isBasic: true, format: 'number' },
      { key: 'fourth_down_conversions', label: '4th Down Conv', isBasic: true, format: 'number' },
      { key: 'red_zone_attempts', label: 'Red Zone Att', isBasic: true, format: 'number' },
      { key: 'red_zone_touchdowns', label: 'Red Zone TD', isBasic: true, format: 'number' },
      { key: 'penalties', label: 'Penalties', isBasic: true, format: 'number' },
      { key: 'penalty_yards', label: 'Penalty Yards', isBasic: true, format: 'number' },
      { key: 'third_down_pct', label: '3rd Down %', isAdvanced: true, isCalculated: true, dependencies: ['third_down_conversions', 'third_down_attempts'], format: 'percentage', decimals: 1 },
      { key: 'fourth_down_pct', label: '4th Down %', isAdvanced: true, isCalculated: true, dependencies: ['fourth_down_conversions', 'fourth_down_attempts'], format: 'percentage', decimals: 1 },
      { key: 'red_zone_pct', label: 'Red Zone %', isAdvanced: true, isCalculated: true, dependencies: ['red_zone_touchdowns', 'red_zone_attempts'], format: 'percentage', decimals: 1 },
      { key: 'turnover_margin', label: 'Turnover Margin', isAdvanced: true, isCalculated: true, dependencies: ['takeaways', 'turnovers'], format: 'number' },
      // Fumble stats (team level)
      { key: 'fumbles', label: 'Fumbles', isBasic: true, format: 'number' },
      { key: 'fumbles_lost', label: 'FUM Lost', isBasic: true, format: 'number' },
      { key: 'fumbles_recovered', label: 'FUM Rec', isBasic: true, format: 'number' },
      // Additional team stats
      { key: 'yards_allowed', label: 'Yards Allowed', isBasic: true, format: 'number' },
      { key: 'points_against_per_game', label: 'PA/G', isAdvanced: true, isCalculated: true, dependencies: ['points_against', 'games_played'], format: 'decimal', decimals: 1 },
    ],
    player: [
      // Passing stats
      { key: 'pass_attempts', label: 'Pass ATT', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_completions', label: 'Completions', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_yards', label: 'Pass YDS', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_touchdowns', label: 'Pass TD', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'interceptions_thrown', label: 'INT Thrown', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_longest', label: 'Pass LNG', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'completion_pct', label: 'CMP%', isAdvanced: true, isCalculated: true, dependencies: ['pass_completions', 'pass_attempts'], format: 'percentage', decimals: 1, positions: ['QB'] },
      { key: 'yards_per_attempt', label: 'YPA', isAdvanced: true, isCalculated: true, dependencies: ['pass_yards', 'pass_attempts'], format: 'decimal', decimals: 1, positions: ['QB'] },
      { key: 'passer_rating', label: 'RTG', isAdvanced: true, isCalculated: true, dependencies: ['pass_completions', 'pass_attempts', 'pass_yards', 'pass_touchdowns', 'interceptions_thrown'], format: 'decimal', decimals: 1, positions: ['QB'], description: 'NFL Passer Rating (may skew high for flag football)' },
      { key: 'interception_rate', label: 'INT%', isAdvanced: true, isCalculated: true, dependencies: ['interceptions_thrown', 'pass_attempts'], format: 'percentage', decimals: 1, positions: ['QB'], description: 'Interceptions per pass attempt' },
      { key: 'sacks_taken', label: 'Sacks Taken', isBasic: true, format: 'number', positions: ['QB'], description: 'QB pressures resulting in loss' },
      // Rushing stats
      { key: 'rush_attempts', label: 'Rush ATT', isBasic: true, format: 'number', positions: ['RB', 'QB', 'WR', 'SLOT'] },
      { key: 'rush_yards', label: 'Rush YDS', isBasic: true, format: 'number', positions: ['RB', 'QB', 'WR', 'SLOT'] },
      { key: 'rush_touchdowns', label: 'Rush TD', isBasic: true, format: 'number', positions: ['RB', 'QB', 'WR', 'SLOT'] },
      { key: 'rush_longest', label: 'Rush LNG', isBasic: true, format: 'number', positions: ['RB', 'QB', 'WR', 'SLOT'] },
      { key: 'yards_per_carry', label: 'YPC', isAdvanced: true, isCalculated: true, dependencies: ['rush_yards', 'rush_attempts'], format: 'decimal', decimals: 1, positions: ['RB', 'QB', 'WR', 'SLOT'] },
      // Receiving stats
      { key: 'targets', label: 'TGT', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'receptions', label: 'REC', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'receiving_yards', label: 'Rec YDS', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'receiving_touchdowns', label: 'Rec TD', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'receiving_longest', label: 'Rec LNG', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'yards_after_catch', label: 'YAC', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'], description: 'Yards gained after the catch' },
      { key: 'catch_rate', label: 'CTCH%', isAdvanced: true, isCalculated: true, dependencies: ['receptions', 'targets'], format: 'percentage', decimals: 1, positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'yards_per_reception', label: 'YPR', isAdvanced: true, isCalculated: true, dependencies: ['receiving_yards', 'receptions'], format: 'decimal', decimals: 1, positions: ['WR', 'RB', 'SLOT', 'C'] },
      { key: 'yards_per_target', label: 'Y/TGT', isAdvanced: true, isCalculated: true, dependencies: ['receiving_yards', 'targets'], format: 'decimal', decimals: 1, positions: ['WR', 'RB', 'SLOT', 'C'], description: 'Receiving yards per target' },
      { key: 'drops', label: 'DRP', isBasic: true, format: 'number', positions: ['WR', 'RB', 'SLOT', 'C'] },
      // Defensive stats
      { key: 'flag_pulls', label: 'Flag Pulls', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'missed_flag_pulls', label: 'Missed FP', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'sacks', label: 'Sacks', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'], description: 'QB pressures resulting in loss' },
      { key: 'interceptions', label: 'DEF INT', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'interception_return_yards', label: 'INT Ret YDS', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'defensive_touchdowns', label: 'DEF TD', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'passes_defended', label: 'Pass DEF', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'qb_pressures', label: 'QB Press', isBasic: true, format: 'number', positions: ['RUSH'] },
      { key: 'flag_pull_pct', label: 'FP%', isAdvanced: true, isCalculated: true, dependencies: ['flag_pulls', 'missed_flag_pulls'], format: 'percentage', decimals: 1, positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      // Fumble stats (optional - rare in flag football)
      { key: 'fumbles', label: 'FUM', isBasic: true, format: 'number', positions: ['QB', 'RB', 'WR', 'SLOT', 'C'], description: 'Rare in flag football - typically bad snaps or failed laterals' },
      { key: 'fumbles_lost', label: 'FUM Lost', isBasic: true, format: 'number', positions: ['QB', 'RB', 'WR', 'SLOT', 'C'] },
      { key: 'forced_fumbles', label: 'FF', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      { key: 'fumble_recoveries', label: 'FR', isBasic: true, format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      // Big play tracking
      { key: 'big_plays', label: 'Big Plays', isBasic: true, format: 'number', description: 'Plays of 20+ yards' },
      // Scoring stats (all players)
      { key: 'touchdowns', label: 'Total TD', isBasic: true, format: 'number' },
      { key: 'extra_points_1pt', label: '1PT Conv', isBasic: true, format: 'number' },
      { key: 'extra_points_2pt', label: '2PT Conv', isBasic: true, format: 'number' },
      { key: 'return_touchdowns', label: 'Return TD', isBasic: true, format: 'number', description: 'Kickoff/punt return touchdowns' },
      
      { key: 'points_per_game', label: 'PPG', isAdvanced: true, isCalculated: true, dependencies: ['total_points', 'games_played'], format: 'decimal', decimals: 1, description: 'Individual scoring average' },
      // Defensive "Stops" stat - sums flag pulls + sacks + interceptions
      { key: 'stops', label: 'Stops', isAdvanced: true, isCalculated: true, dependencies: ['flag_pulls', 'sacks', 'interceptions'], format: 'number', positions: ['RUSH', 'DB', 'S', 'CB', 'LB'] },
      // Additional advanced stats
      { key: 'td_int_ratio', label: 'TD/INT', isAdvanced: true, isCalculated: true, dependencies: ['pass_touchdowns', 'interceptions_thrown'], format: 'decimal', decimals: 2, positions: ['QB'] },
      { key: 'total_offensive_yards', label: 'Total Off YDS', isAdvanced: true, isCalculated: true, dependencies: ['pass_yards', 'rush_yards'], format: 'number', positions: ['QB'] },
      { key: 'big_play_rate', label: 'Big Play %', isAdvanced: true, isCalculated: true, dependencies: ['big_plays', 'rush_attempts', 'receptions'], format: 'percentage', decimals: 1, description: 'Big plays per touch' },
    ],
  },
  seasonStats: {
    team: [
      { key: 'wins', label: 'Wins', isBasic: true, format: 'number' },
      { key: 'losses', label: 'Losses', isBasic: true, format: 'number' },
      { key: 'ties', label: 'Ties', isBasic: true, format: 'number' },
      { key: 'points_for', label: 'Points For', isBasic: true, format: 'number' },
      { key: 'points_against', label: 'Points Against', isBasic: true, format: 'number' },
      { key: 'point_differential', label: 'Point Diff', isAdvanced: true, isCalculated: true, dependencies: ['points_for', 'points_against'], format: 'number' },
      { key: 'touchdowns', label: 'Touchdowns', isBasic: true, format: 'number' },
      { key: 'touchdowns_allowed', label: 'TDs Allowed', isBasic: true, format: 'number' },
      { key: 'ppg', label: 'PPG', isAdvanced: true, isCalculated: true, dependencies: ['points_for', 'games_played'], format: 'decimal', decimals: 1 },
    ],
    player: [
      { key: 'games_played', label: 'Games', isBasic: true, format: 'number' },
      // Passing season stats
      { key: 'pass_attempts', label: 'Pass ATT', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_completions', label: 'Completions', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_yards', label: 'Pass YDS', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'pass_touchdowns', label: 'Pass TD', isBasic: true, format: 'number', positions: ['QB'] },
      { key: 'interceptions_thrown', label: 'INT Thrown', isBasic: true, format: 'number', positions: ['QB'] },
      // Rushing season stats
      { key: 'rush_attempts', label: 'Rush ATT', isBasic: true, format: 'number' },
      { key: 'rush_yards', label: 'Rush YDS', isBasic: true, format: 'number' },
      { key: 'rush_touchdowns', label: 'Rush TD', isBasic: true, format: 'number' },
      // Receiving season stats
      { key: 'receptions', label: 'REC', isBasic: true, format: 'number' },
      { key: 'receiving_yards', label: 'Rec YDS', isBasic: true, format: 'number' },
      { key: 'receiving_touchdowns', label: 'Rec TD', isBasic: true, format: 'number' },
      // Defensive season stats
      { key: 'flag_pulls', label: 'Flag Pulls', isBasic: true, format: 'number' },
      { key: 'sacks', label: 'Sacks', isBasic: true, format: 'number' },
      { key: 'interceptions', label: 'DEF INT', isBasic: true, format: 'number' },
      { key: 'defensive_touchdowns', label: 'DEF TD', isBasic: true, format: 'number' },
      // Totals
      { key: 'touchdowns', label: 'Total TD', isBasic: true, format: 'number' },
      
      // Advanced
      { key: 'passer_rating', label: 'RTG', isAdvanced: true, isCalculated: true, dependencies: ['pass_completions', 'pass_attempts', 'pass_yards', 'pass_touchdowns', 'interceptions_thrown'], format: 'decimal', decimals: 1, positions: ['QB'] },
      { key: 'yards_per_attempt', label: 'YPA', isAdvanced: true, isCalculated: true, dependencies: ['pass_yards', 'pass_attempts'], format: 'decimal', decimals: 1, positions: ['QB'] },
      { key: 'yards_per_carry', label: 'YPC', isAdvanced: true, isCalculated: true, dependencies: ['rush_yards', 'rush_attempts'], format: 'decimal', decimals: 1 },
      { key: 'yards_per_reception', label: 'YPR', isAdvanced: true, isCalculated: true, dependencies: ['receiving_yards', 'receptions'], format: 'decimal', decimals: 1 },
    ],
  },
  defaultEnabledStats: {
    // Note: turnovers/takeaways are now calculated from player stats (see statAggregator.ts)
    // Added team passing stats and defensive stops per assessment recommendations
    teamBasic: ['touchdowns', 'total_yards', 'pass_yards', 'rush_yards', 'pass_completions', 'pass_attempts', 'pass_touchdowns', 'rush_touchdowns', 'turnovers', 'takeaways', 'sacks', 'interceptions', 'defensive_stops', 'first_downs', 'third_down_attempts', 'third_down_conversions', 'fourth_down_attempts', 'fourth_down_conversions', 'red_zone_attempts', 'red_zone_touchdowns', 'penalties', 'penalty_yards', 'safeties', 'yards_allowed'],
    teamAdvanced: ['third_down_pct', 'fourth_down_pct', 'red_zone_pct', 'turnover_margin', 'yards_per_play', 'ppg', 'points_against_per_game'],
    // Removed fumbles from default (optional - rare in flag football), added yards_after_catch
    playerBasic: ['pass_attempts', 'pass_completions', 'pass_yards', 'pass_touchdowns', 'interceptions_thrown', 'rush_attempts', 'rush_yards', 'rush_touchdowns', 'targets', 'receptions', 'receiving_yards', 'receiving_touchdowns', 'yards_after_catch', 'drops', 'flag_pulls', 'missed_flag_pulls', 'sacks', 'interceptions', 'interception_return_yards', 'defensive_touchdowns', 'passes_defended', 'qb_pressures', 'touchdowns'],
    // Added new advanced stats: interception_rate, yards_per_target, points_per_game
    playerAdvanced: ['completion_pct', 'yards_per_attempt', 'passer_rating', 'td_int_ratio', 'interception_rate', 'yards_per_carry', 'catch_rate', 'yards_per_reception', 'yards_per_target', 'flag_pull_pct', 'points_per_game', 'total_offensive_yards', 'stops'],
    playerEssential: ['games_played', 'pass_yards', 'pass_touchdowns', 'rush_yards', 'rush_touchdowns', 'receiving_yards', 'receiving_touchdowns', 'flag_pulls', 'interceptions', 'touchdowns'],
    teamEssential: ['games_played', 'touchdowns', 'total_yards', 'turnovers', 'takeaways', 'defensive_stops'],
  },
  statSynonyms: {
    // Passing
    pass_attempts: ['att', 'pass_att', 'attempts'],
    pass_completions: ['cmp', 'completions', 'comp'],
    pass_yards: ['passing_yards', 'yds', 'yards'],
    pass_touchdowns: ['passing_tds', 'td', 'tds'],
    interceptions_thrown: ['int_thrown', 'ints_thrown', 'thrown_interceptions'], // Note: 'int' reserved for defensive interceptions
    completion_pct: ['cmp_pct', 'comp_pct', 'completion_percentage'],
    yards_per_attempt: ['ypa', 'y/a'],
    passer_rating: ['qb_rating', 'rtg', 'rating'],
    sacks_taken: ['sck_taken', 'sacks'],
    interception_rate: ['int_rate', 'int_pct', 'interception_pct'],
    // Rushing
    rush_attempts: ['rushes', 'carries', 'rush_att'],
    rush_yards: ['rushing_yards', 'rush_yds'],
    rush_touchdowns: ['rushing_tds', 'rush_td'],
    yards_per_carry: ['ypc', 'y/c', 'avg'],
    // Receiving
    receptions: ['rec', 'catches'],
    receiving_yards: ['rec_yards', 'rec_yds'],
    receiving_touchdowns: ['rec_tds', 'rec_td'],
    targets: ['tgt', 'tgts'],
    catch_rate: ['ctch_pct', 'catch_pct'],
    yards_per_reception: ['ypr', 'y/r', 'avg_rec'],
    yards_per_target: ['yds_per_tgt', 'y_per_tgt', 'yards_target'],
    yards_after_catch: ['yac', 'after_catch_yards'],
    // Defensive
    flag_pulls: ['fp', 'tackles', 'tkl'],
    missed_flag_pulls: ['mfp', 'missed_pulls', 'missed_tackles'],
    sacks: ['sck', 'qb_sacks'],
    interceptions: ['int', 'picks'],
    defensive_touchdowns: ['def_td', 'dtd', 'pick_six'],
    passes_defended: ['pd', 'pbu', 'pass_breakups'],
    qb_pressures: ['pres', 'pressures', 'hurries'],
    defensive_stops: ['def_stops', 'stops_team'],
    // Fumbles
    fumbles: ['fum'],
    fumbles_lost: ['fum_lost', 'fl'],
    forced_fumbles: ['ff', 'forced_fum'],
    fumble_recoveries: ['fr', 'fum_rec'],
    // Advanced
    td_int_ratio: ['td_to_int', 'tdi'],
    total_offensive_yards: ['tot_off_yds', 'total_off_yards'],
    points_against_per_game: ['papg', 'pa_per_game'],
    yards_allowed: ['yds_allowed', 'def_yards'],
    safeties: ['saf', 'safety'],
    flag_pull_pct: ['fp_pct', 'tackle_pct'],
    points_per_game: ['ppg_player', 'pts_per_game'],
    big_plays: ['big_play', 'explosive_plays'],
    big_play_rate: ['big_play_pct', 'explosive_rate'],
    yards_per_play: ['ypp', 'y_per_play'],
    // Scoring
    touchdowns: ['tds', 'td'],
    extra_points_1pt: ['xp1', 'pat1', '1pt'],
    extra_points_2pt: ['xp2', 'pat2', '2pt'],
    total_points: ['pts', 'points'],
    return_touchdowns: ['ret_td', 'return_td'],
    // Team
    total_yards: ['yards', 'tot_yards'],
    total_plays: ['plays', 'tot_plays'],
    first_downs: ['1st_downs', 'fd'],
    turnovers: ['to', 'giveaways'],
    takeaways: ['tko', 'forced_turnovers'],
    points_for: ['pf', 'pts_for'],
    points_against: ['pa', 'pts_against'],
    point_differential: ['diff', 'pt_diff'],
    third_down_pct_allowed: ['3rd_pct_allowed', 'def_3rd_down_pct'],
    scoring_efficiency: ['scoring_eff', 'rz_scoring'],
  },
  statDisplayOrder: {
    // Updated to include new stats in logical ESPN-style order
    player: ['games_played', 'pass_attempts', 'pass_completions', 'completion_pct', 'pass_yards', 'yards_per_attempt', 'pass_touchdowns', 'interceptions_thrown', 'interception_rate', 'passer_rating', 'td_int_ratio', 'rush_attempts', 'rush_yards', 'yards_per_carry', 'rush_touchdowns', 'targets', 'receptions', 'catch_rate', 'receiving_yards', 'yards_per_reception', 'yards_per_target', 'yards_after_catch', 'receiving_touchdowns', 'drops', 'big_plays', 'big_play_rate', 'flag_pulls', 'missed_flag_pulls', 'flag_pull_pct', 'sacks', 'interceptions', 'interception_return_yards', 'defensive_touchdowns', 'passes_defended', 'qb_pressures', 'stops', 'touchdowns', 'return_touchdowns', 'extra_points_1pt', 'extra_points_2pt', 'total_points', 'points_per_game'],
    team: ['games_played', 'wins', 'losses', 'ties', 'points', 'points_for', 'points_against', 'ppg', 'points_against_per_game', 'point_differential', 'touchdowns', 'pass_touchdowns', 'rush_touchdowns', 'touchdowns_allowed', 'total_yards', 'pass_yards', 'rush_yards', 'pass_completions', 'pass_attempts', 'total_plays', 'yards_per_play', 'first_downs', 'third_down_attempts', 'third_down_conversions', 'third_down_pct', 'fourth_down_attempts', 'fourth_down_conversions', 'fourth_down_pct', 'red_zone_attempts', 'red_zone_touchdowns', 'red_zone_pct', 'turnovers', 'takeaways', 'turnover_margin', 'sacks', 'interceptions', 'defensive_stops', 'penalties', 'penalty_yards', 'safeties'],
  }
};

// Sport Configurations Map
export const SPORT_CONFIGS: Record<SportType, SportConfig> = {
  soccer: soccerConfig,
  basketball: basketballConfig,
  baseball: baseballConfig,
  softball: softballConfig,
  flag_football: flagFootballConfig,
};

// Export individual configs for direct access
export { soccerConfig, basketballConfig, baseballConfig, softballConfig, flagFootballConfig };

/**
 * Get sport configuration
 */
export function getSportConfig(sport: SportType): SportConfig {
  return SPORT_CONFIGS[sport] || SPORT_CONFIGS.soccer;
}

/**
 * Check if a position is a pitcher position
 */
export const isPitcherPosition = (position: string | undefined, sport: SportType): boolean => {
  if (!position) return false;
  const config = getSportConfig(sport);
  if (!config.positionGroups?.pitchers) return false;
  
  // Normalize position using aliases
  const normalizedPos = config.positionAliases?.[position] || position.toUpperCase();
  return config.positionGroups.pitchers.includes(normalizedPos);
};

/**
 * Get batting stats for a sport (excludes pitcher-only stats)
 */
export const getBattingStats = (sport: SportType): string[] => {
  const config = getSportConfig(sport);
  return config.matchStats?.player
    ?.filter(s => !s.positions || !s.positions.includes('P'))
    .map(s => s.key) || [];
};

/**
 * Get pitching stats for a sport (pitcher-only stats)
 */
export const getPitchingStats = (sport: SportType): string[] => {
  const config = getSportConfig(sport);
  return config.matchStats?.player
    ?.filter(s => s.positions?.includes('P'))
    .map(s => s.key) || [];
};

/**
 * Orders stats according to sport-specific ESPN/CBS conventions
 */
export function orderStatsForDisplay(
  stats: string[],
  sport: SportType,
  scope: 'player' | 'team',
  position?: string
): string[] {
  const config = getSportConfig(sport);
  
  // Get the canonical order
  let canonicalOrder: string[];
  
  // For soccer goalkeepers, use special ordering
  if (sport === 'soccer' && scope === 'player' && position === 'GK' && config.statDisplayOrder.playerGK) {
    canonicalOrder = config.statDisplayOrder.playerGK;
  } else {
    canonicalOrder = config.statDisplayOrder[scope];
  }
  
  // Create ordered list based on canonical order
  const ordered: string[] = [];
  const remaining = new Set(stats);
  
  // First, add stats in canonical order
  for (const statKey of canonicalOrder) {
    // Resolve synonyms
    const resolved = resolveStatKey(sport, statKey);
    if (remaining.has(resolved)) {
      ordered.push(resolved);
      remaining.delete(resolved);
    } else if (remaining.has(statKey)) {
      ordered.push(statKey);
      remaining.delete(statKey);
    }
  }
  
  // Then add any remaining stats that weren't in canonical order
  remaining.forEach(stat => ordered.push(stat));
  
  return ordered;
}

/**
 * Resolve a free-text sport value to a configured SportType (one of the five
 * sports with full stat configs), or null when it doesn't map.
 *
 * Normalization (case, spaces, hyphens, aliases) is delegated to the
 * canonical sport registry.
 */
export function toConfiguredSportType(value: string | null | undefined): SportType | null {
  switch (normalizeSport(value)) {
    case 'soccer':
      return 'soccer';
    case 'basketball':
      return 'basketball';
    case 'baseball':
      return 'baseball';
    case 'softball':
      return 'softball';
    case 'flag_football':
      return 'flag_football';
    default:
      return null;
  }
}

/**
 * Detect sport from various indicators
 */
export function detectSport(indicators: {
  sportProp?: string;
  scoringPeriods?: any[];
  leagueSettings?: any;
}): SportType {
  const { sportProp, scoringPeriods, leagueSettings } = indicators;

  // Direct sport property
  if (sportProp) {
    const detected = toConfiguredSportType(sportProp);
    if (detected) return detected;

    // Invalid sport provided - log error
    console.error('[SPORT DETECTION ERROR] Invalid sport value:', sportProp, 'Must be one of: soccer, basketball, baseball, softball, flag_football');
  }

  // From league settings
  if (leagueSettings?.sport) {
    const detected = toConfiguredSportType(leagueSettings.sport);
    if (detected) return detected;
  }

  // From scoring periods
  if (scoringPeriods && Array.isArray(scoringPeriods)) {
    const periods = scoringPeriods.map(p => String(p).toLowerCase());
    if (periods.some(p => p.includes('half'))) return 'soccer';
    if (periods.some(p => p.includes('q') || p.includes('quarter'))) return 'basketball';
    if (periods.every(p => /^\d+$/.test(p))) {
      return periods.length > 7 ? 'baseball' : 'softball';
    }
  }

  // Default fallback with warning
  console.warn('[SPORT DETECTION] Could not detect sport from params:', {
    sportProp,
    hasLeagueSettings: !!leagueSettings,
    hasScoringPeriods: !!scoringPeriods
  }, '- Defaulting to soccer');
  // Default to soccer to stay consistent with getSportConfig's fallback and
  // because soccer allows ties (the most forgiving rule for an unknown sport).
  return 'soccer';
}

/**
 * Get all stat definitions for a sport/context
 */
export function getStatDefinitions(
  sport: SportType,
  context: 'match' | 'season',
  type: 'team' | 'player'
): StatDefinition[] {
  const config = getSportConfig(sport);
  return context === 'match' ? config.matchStats[type] : config.seasonStats[type];
}

/**
 * Get stat definition by key
 */
export function getStatDefinition(
  sport: SportType,
  context: 'match' | 'season',
  type: 'team' | 'player',
  statKey: string
): StatDefinition | undefined {
  const definitions = getStatDefinitions(sport, context, type);
  return definitions.find(d => d.key === statKey);
}

/**
 * Stat keys that are auto-derived from the schedule (game counts) rather than
 * entered by a scorekeeper. They are always available as a dependency and must
 * never block enabling an advanced/per-game stat. Sport-generic: any per-game
 * average across sports (PPG, goals-per-game, etc.) depends on a games count.
 */
export const AUTO_DERIVED_STAT_KEYS: readonly string[] = [
  'games_played',
  'games',
  'gp',
  'games_started',
  'gs',
];

/**
 * Whether a single declared dependency `dep` of a calculated stat can be
 * satisfied for a league whose tracked (selected) stats are `trackedStats`.
 * Resolution is sport-generic and RECURSIVE:
 *
 *  1. Auto-derived stats (games played / started) come from the schedule, so
 *     they are always available.
 *  2. `opponent_*` dependencies (e.g. `opponent_dreb`, `opponent_treb`) are read
 *     from the opposing team's box score at calculation time, so they resolve
 *     whenever their base stat (the part after `opponent_`) is available.
 *  3. A dependency that is itself a CALCULATED stat (e.g. OPS depends on OBP and
 *     SLG) resolves when ITS OWN inputs are available, resolved recursively via
 *     `isStatAvailable`. This is what lets advanced-on-advanced metrics be
 *     enabled once their raw inputs are tracked, instead of being permanently
 *     blocked by a dependency that is never itself a raw basic stat.
 *  4. Any other (raw counting) stat resolves iff the league tracks it.
 *
 * `seen` guards against dependency cycles across the recursion.
 */
function isDependencyResolvable(
  sport: SportType,
  type: 'team' | 'player',
  dep: string,
  trackedStats: Set<string>,
  seen: Set<string> = new Set()
): boolean {
  if (AUTO_DERIVED_STAT_KEYS.includes(dep)) return true;

  if (dep.startsWith('opponent_')) {
    const baseKey = dep.slice('opponent_'.length);
    // The opponent value is pulled from the other team's box score, so it is
    // available whenever the base stat is available (or itself auto-derived).
    return (
      AUTO_DERIVED_STAT_KEYS.includes(baseKey) ||
      isStatAvailable(sport, type, baseKey, trackedStats, seen)
    );
  }

  return isStatAvailable(sport, type, dep, trackedStats, seen);
}

/**
 * Filter a calculated stat's dependency list down to the dependencies that
 * CANNOT be satisfied for a league tracking `trackedStats` (its selected basic
 * stats), resolving each dependency RECURSIVELY (see `isDependencyResolvable`).
 * The recursive analog of the old flat `isDependencyAvailable` check.
 *
 * This is the predicate the Advanced Statistics config UI uses to decide whether
 * an advanced stat can be enabled and what its "Requires:" hint lists. Because
 * resolution is recursive, an advanced stat that depends on OTHER calculated
 * stats (e.g. baseball/softball OPS = OBP + SLG, for any sport) becomes
 * selectable once the underlying raw inputs are tracked, rather than being
 * impossible to select because OBP/SLG are not themselves raw basic stats.
 * Auto-derived and opponent-resolved dependencies are never reported as missing.
 */
export function getUnresolvableDependencies(
  sport: SportType,
  type: 'team' | 'player',
  dependencies: string[] | undefined,
  trackedStats: Set<string>
): string[] {
  if (!dependencies || dependencies.length === 0) return [];
  return dependencies.filter(
    (dep) => !isDependencyResolvable(sport, type, dep, trackedStats, new Set())
  );
}

/**
 * Whether a season / advanced stat COLUMN has data to show for a league that
 * tracks `trackedStats` (a synonym-resolved set of the raw stats the league
 * enabled in Scoring & Stats). This is the single predicate the config-driven
 * public stat tables use so they render ONLY the metrics a league enabled, for
 * every sport, instead of the full sport catalogue.
 *
 * Rules (sport-generic):
 *  - Auto-derived counts (games played / started) are always available.
 *  - A CALCULATED stat (percentage, per-game rate, ratio, advanced metric) is
 *    available iff every one of its dependencies is available, resolved
 *    RECURSIVELY. So OPS shows once OBP and SLG can be computed, which in turn
 *    need their raw inputs tracked; PPG shows once points is tracked. Merely
 *    listing a rate in the league's advanced stats is NOT enough on its own: a
 *    rate whose raw inputs are untracked has no data and stays hidden.
 *  - `opponent_*` dependencies come from the opposing box score, so they are
 *    available whenever their base stat is.
 *  - Any other (raw counting) stat is available iff the league tracks it.
 *
 * `_seen` guards against dependency cycles; each branch is given its own copy so
 * sibling dependencies that share a sub-dependency are evaluated independently.
 */
export function isStatAvailable(
  sport: SportType,
  type: 'team' | 'player',
  statKey: string,
  trackedStats: Set<string>,
  _seen: Set<string> = new Set()
): boolean {
  const resolved = resolveStatKey(sport, statKey);
  if (AUTO_DERIVED_STAT_KEYS.includes(resolved)) return true;

  const def =
    getStatDefinition(sport, 'season', type, resolved) ||
    getStatDefinition(sport, 'match', type, resolved);

  if (def?.isCalculated && def.dependencies && def.dependencies.length > 0) {
    if (_seen.has(resolved)) return false;
    const seen = new Set(_seen).add(resolved);
    return def.dependencies.every((dep) =>
      isDependencyResolvable(sport, type, dep, trackedStats, seen)
    );
  }

  return trackedStats.has(resolved);
}

/**
 * Resolve stat key using synonyms
 */
export function resolveStatKey(sport: SportType, statKey: string): string {
  if (!statKey) return statKey;
  const config = getSportConfig(sport);
  const normalized = statKey.toLowerCase();
  
  for (const [canonical, synonyms] of Object.entries(config.statSynonyms)) {
    if (canonical === normalized || synonyms.includes(normalized)) {
      return canonical;
    }
  }
  
  return normalized;
}

/**
 * Normalize position using aliases (case-insensitive)
 *
 * `extraPositions` is a league-scoped addition on top of the sport's preset
 * list (see `league_settings.custom_positions`, added by
 * `20260921090000_league_custom_positions.sql`), checked alongside
 * `config.positions` and before the alias table lookup, so a custom
 * position is recognized on the same footing as a preset one. Optional, so
 * no existing caller needs to change.
 */
export function normalizePosition(
  sport: SportType,
  position: string | undefined | null,
  extraPositions?: string[]
): string | undefined {
  if (!position) return undefined;

  const config = getSportConfig(sport);
  const positionUpper = position.toUpperCase();
  const extras = extraPositions || [];

  // If it's already a valid abbreviated position (preset or league custom), return it
  if (config.positions.includes(position) || extras.includes(position)) {
    return position;
  }

  // Also check uppercase version (e.g., "GK" matches even if positions is ['GK'])
  const upperMatch =
    config.positions.find(p => p.toUpperCase() === positionUpper) ||
    extras.find(p => p.toUpperCase() === positionUpper);
  if (upperMatch) {
    return upperMatch;
  }

  // Check if it's an alias (case-insensitive)
  for (const [alias, canonical] of Object.entries(config.positionAliases)) {
    if (alias.toLowerCase() === position.toLowerCase()) {
      return canonical;
    }
  }

  // Return original if no match found
  return position;
}

/**
 * Get label for a stat key
 */
export function getStatLabel(sport: SportType, statKey: string): string {
  if (!statKey) return '';
  const resolved = resolveStatKey(sport, statKey);
  
  // Check match stats
  const matchTeamDef = getStatDefinition(sport, 'match', 'team', resolved);
  if (matchTeamDef) return matchTeamDef.label;
  
  const matchPlayerDef = getStatDefinition(sport, 'match', 'player', resolved);
  if (matchPlayerDef) return matchPlayerDef.label;
  
  // Check season stats
  const seasonTeamDef = getStatDefinition(sport, 'season', 'team', resolved);
  if (seasonTeamDef) return seasonTeamDef.label;
  
  const seasonPlayerDef = getStatDefinition(sport, 'season', 'player', resolved);
  if (seasonPlayerDef) return seasonPlayerDef.label;
  
  // Fallback to formatted key
  return statKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

/**
 * Get sport-specific scoring terminology
 */
export function getScoringTerminology(sport: SportType): ScoringTerminology {
  // Sourced from the canonical sport registry so the scoring noun
  // (goals / runs / points) is defined exactly once per sport.
  return getRegistryScoringTerminology(sport);
}

/**
 * Filter stats by position
 */
export function getStatsForPosition(
  sport: SportType,
  context: 'match' | 'season',
  position: string
): StatDefinition[] {
  const allStats = getStatDefinitions(sport, context, 'player');
  return allStats.filter(stat => 
    !stat.positions || stat.positions.length === 0 || stat.positions.includes(position)
  );
}

/**
 * Get the full display label for a position abbreviation
 * Returns the abbreviation if no label is found
 */
export function getPositionLabel(
  sport: SportType,
  position: string | undefined | null,
  extraPositions?: string[]
): string {
  if (!position) return '';

  const config = getSportConfig(sport);

  // First normalize the position to get the canonical abbreviation
  const normalizedPosition = normalizePosition(sport, position, extraPositions);
  if (!normalizedPosition) return position;

  // Look up the display label. A league custom position with no preset
  // label falls back to the raw token, which is the point of extraPositions.
  return config.positionLabels?.[normalizedPosition] || normalizedPosition;
}

/**
 * Check if a sport allows tie games
 */
export function sportAllowsTies(sport: SportType | string): boolean {
  // Tie rule is defined once per sport in the canonical sport registry.
  return registrySportAllowsTies(sport);
}

/**
 * Format a team record string based on sport conventions
 * Soccer: W-D-L (Wins-Draws-Losses)
 * Flag Football & other tie-allowing sports: W-L-T
 * Others: W-L (or W-L-T if ties exist)
 */
export function formatRecord(record: { wins: number; losses: number; ties?: number }, sport?: string): string {
  const w = record.wins ?? 0;
  const l = record.losses ?? 0;
  const t = record.ties ?? 0;
  const isSoccer = sport?.toLowerCase() === 'soccer';
  const allowsTies = sport ? sportAllowsTies(sport) : false;

  if (isSoccer) {
    return `${w}-${t}-${l}`; // W-D-L
  }
  if (allowsTies || t > 0) {
    return `${w}-${l}-${t}`; // W-L-T
  }
  return `${w}-${l}`;
}
